import React from "react";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { documentService } from "@/app/providers/services";
import { useUpdateFullRelease } from "@/common/hooks_useFullRelease";
import { useNotification } from "@/common/notifications";
import { useScope } from "@/redux/scope";
import { DERIVED_TABLES_FOLDER } from "@packages/commons";
import { normalizeDerivedTable, type DerivedTable } from "./types";

/**
 * Current draft + last saved state for derived tables, keyed by a table's
 */
export interface DerivedTableEditorEntry {
  /** The saved document's own id, or undefined if this table has never been saved. */
  documentId?: string;
  /** The last known saved server contents - the baseline `draft` is compared against for isDirty. */
  document: DerivedTable;
  /** lastModified of the document record as of the last sync - the basis for staleness detection. */
  lastModified?: string;
  /** Current draft is persisted to local storage so it survives a refresh. May or may not be saved */
  draft: DerivedTable;
}

/** Entries for a single release, keyed by table id. */
export type DerivedTableEditorState = Record<string, DerivedTableEditorEntry>;

/**
 * Top-level persisted shape: entries segmented by release id. This is what actually gets
 * persisted to local storage, so without this outer layer a draft table opened in one
 * release/project would keep showing up (and be editable) in every other release you visit -
 * the store is shared across the whole app, but a draft only makes sense in the release it
 * belongs to.
 */
export type DerivedTableEditorStoreState = Record<string, DerivedTableEditorState>;

export const REDUX_PROP = "derivedTableEditor";

const EMPTY_ENTRIES: DerivedTableEditorState = {};

const sameContents = (a: DerivedTable, b: DerivedTable) => JSON.stringify(a) === JSON.stringify(b);

/** A saved derived table as currently held by the server. */
export interface SyncedDerivedTable {
  id: string;
  documentId: string;
  document: DerivedTable;
  lastModified: string;
}

/** Refreshes an entry from the server, keeping any unsaved draft. */
const upsertLoaded = (releaseState: DerivedTableEditorState, { id, documentId, document, lastModified }: SyncedDerivedTable) => {
  const existing = releaseState[id];
  const wasDirty = existing ? !sameContents(existing.draft, existing.document) : false;
  releaseState[id] = {
    documentId,
    document,
    lastModified,
    draft: wasDirty ? (existing as DerivedTableEditorEntry).draft : document,
  };
};

const editorSlice = createSlice({
  name: REDUX_PROP,
  initialState: {} as DerivedTableEditorStoreState,
  reducers: {
    tableLoaded: (
      state,
      action: PayloadAction<{ release: string; id: string; documentId: string; document: DerivedTable; lastModified: string }>,
    ) => {
      const { release, ...table } = action.payload;
      upsertLoaded((state[release] ??= {}), table);
    },
    /**
     * Reconciles a release with the complete list of its saved tables: refreshes each one, and drops
     * any previously-saved entry the server no longer has (deleted here or elsewhere). Unsaved local
     * tables (no documentId) are kept. `tables` must be the full list - a partial page would prune
     * tables that still exist.
     */
    tablesSynced: (state, action: PayloadAction<{ release: string; tables: SyncedDerivedTable[] }>) => {
      const { release, tables } = action.payload;
      const releaseState = (state[release] ??= {});
      const liveIds = new Set(tables.map((t) => t.id));
      for (const [id, entry] of Object.entries(releaseState)) {
        if (entry.documentId && !liveIds.has(id)) delete releaseState[id];
      }
      for (const table of tables) upsertLoaded(releaseState, table);
    },
    tableCreatedLocally: (state, action: PayloadAction<{ release: string; table: DerivedTable }>) => {
      const { release, table } = action.payload;
      const releaseState = (state[release] ??= {});
      releaseState[table.id] = { documentId: undefined, document: table, lastModified: undefined, draft: table };
    },
    draftChanged: (state, action: PayloadAction<{ release: string; id: string; draft: DerivedTable }>) => {
      const { release, id, draft } = action.payload;
      if (state[release]?.[id]) state[release][id].draft = draft;
    },
    tableSaved: (
      state,
      action: PayloadAction<{ release: string; id: string; documentId: string; document: DerivedTable; lastModified: string }>,
    ) => {
      const { release, id, documentId, document, lastModified } = action.payload;
      const releaseState = (state[release] ??= {});
      releaseState[id] = { documentId, document, lastModified, draft: document };
    },
    tableRemoved: (state, action: PayloadAction<{ release: string; id: string }>) => {
      const { release, id } = action.payload;
      if (state[release]) delete state[release][id];
    },
  },
});

export const { tableLoaded, tablesSynced, tableCreatedLocally, draftChanged, tableSaved, tableRemoved } = editorSlice.actions;

/** The unwrapped reducer, exported for tests */
export const editorReducer = editorSlice.reducer;

/** Persisted to localStorage so an unsaved edit survives a refresh/navigation-away. */
export default persistReducer({ key: "immi.derivedTableEditor", storage }, editorSlice.reducer);

const useDerivedTableEditorStore = (): DerivedTableEditorStoreState => useSelector((s: any) => s[REDUX_PROP] as DerivedTableEditorStoreState);

/** Entries for the current release only - a draft from a different release/project is never visible here. */
export const useDerivedTableEntries = (): DerivedTableEditorState => {
  const { release } = useScope();
  const store = useDerivedTableEditorStore();
  return (release && store[release]) || EMPTY_ENTRIES;
};

const PAGE_SIZE = 100;

type DerivedTableDocument = { id: string; contents?: DerivedTable; lastModified: string; deleted?: boolean };

/**
 * Fetches every saved derived table in a release, following pagination to the end, since the list is
 * used to prune tables that no longer exist. Documents that are pending deletion (soft-deleted until the
 * deleteDocument job removes them) are left out.
 */
export const fetchAllDerivedTables = async (release: string): Promise<SyncedDerivedTable[]> => {
  const docs: DerivedTableDocument[] = [];
  for (let page = 0; ; page++) {
    const res = await documentService.getList({ release, type: "derivedtable", pagination: { page, perPage: PAGE_SIZE } });
    const pageDocs = (res.data ?? []) as DerivedTableDocument[];
    docs.push(...pageDocs);
    if (pageDocs.length < PAGE_SIZE || (res.total !== undefined && docs.length >= res.total)) break;
  }
  return docs
    .filter((doc) => !doc.deleted && doc.contents?.id)
    .map((doc) => {
      const document = normalizeDerivedTable(doc.contents as DerivedTable);
      return { id: document.id, documentId: doc.id, document, lastModified: doc.lastModified };
    });
};

// Prefixed with the document service key so its create/update/delete mutations invalidate this too.
const derivedTablesQueryKey = (release: string | undefined) => [documentService.queryKey, "derivedtable:all", release];

/**
 * Forces a fresh fetch of the release's derived tables. A fetch already in flight may have read the
 * server before a save/delete landed, so it is cancelled rather than reused.
 */
const useRefetchDerivedTables = () => {
  const queryClient = useQueryClient();
  return (release: string) => queryClient.invalidateQueries(derivedTablesQueryKey(release), undefined, { cancelRefetch: true });
};

/** Every derived table for the current release*/
export const useDerivedTables = () => {
  const { release } = useScope();
  const dispatch = useDispatch();
  const entries = useDerivedTableEntries();
  const { data, isLoading } = useQuery(
    derivedTablesQueryKey(release),
    () => fetchAllDerivedTables(release as string),
    { enabled: Boolean(release) },
  );

  React.useEffect(() => {
    if (!release || !data) return;
    dispatch(tablesSynced({ release, tables: data }));
  }, [data, dispatch, release]);

  const tables = React.useMemo(
    () => Object.values(entries).map((e) => e.draft).filter((t): t is DerivedTable => Boolean(t)),
    [entries],
  );
  return { tables, isLoading };
};

export const useDerivedTableEntry = (id: string): DerivedTableEditorEntry | undefined => {
  const entries = useDerivedTableEntries();
  return entries[id];
};

export const useStartNewDerivedTable = () => {
  const { release } = useScope();
  const dispatch = useDispatch();
  return (table: DerivedTable) => {
    if (!release) return;
    dispatch(tableCreatedLocally({ release, table }));
  };
};

export const useSetDerivedTableDraft = () => {
  const { release } = useScope();
  const dispatch = useDispatch();
  return (id: string, draft: DerivedTable) => {
    if (!release) return;
    dispatch(draftChanged({ release, id, draft }));
  };
};

export const useSaveDerivedTable = () => {
  const { release } = useScope();
  const dispatch = useDispatch();
  const updateRelease = useUpdateFullRelease();
  const showNotification = useNotification();
  const { mutateAsync: create } = documentService.useCreate();
  const { mutateAsync: update } = documentService.useUpdate();
  const refetchDerivedTables = useRefetchDerivedTables();

  return async (id: string, documentId: string | undefined, table: DerivedTable) => {
    if (!release) throw new Error("Cannot save a derived table without a release selected.");
    try {
      const res = documentId
        ? await update({ id: documentId, payload: { contents: table } })
        : // @ts-ignore type/contents are correct, TS can't narrow the union from a runtime "type" value
          await create({ release, name: `/${DERIVED_TABLES_FOLDER}/${table.id}`, type: "derivedtable", contents: table });
      const saved = res as unknown as { id: string; contents: DerivedTable; lastModified: string };
      const document = normalizeDerivedTable(saved.contents);
      dispatch(tableSaved({ release, id, documentId: saved.id, document, lastModified: saved.lastModified }));
      return saved;
    } catch (e) {
      showNotification("Failed to save derived table", "error");
      throw e;
    } finally {
      refetchDerivedTables(release);
      updateRelease();
    }
  };
};

export const useDeleteDerivedTable = () => {
  const { release } = useScope();
  const dispatch = useDispatch();
  const updateRelease = useUpdateFullRelease();
  const showNotification = useNotification();
  const { mutateAsync } = documentService.useDelete({ meta: { release } });
  const refetchDerivedTables = useRefetchDerivedTables();

  return async (id: string, documentId: string | undefined) => {
    if (!release) throw new Error("Cannot delete a derived table without a release selected.");
    try {
      if (documentId) await mutateAsync({ id: documentId, payload: {}, params: { release } });
    } catch (e) {
      showNotification("Failed to delete derived table", "error");
      throw e;
    } finally {
      dispatch(tableRemoved({ release, id }));
      refetchDerivedTables(release);
      updateRelease();
    }
  };
};
